// Ficha do merchant (superAdmin): header de identificação + tabs
// (Dados | Endereço | Banco | Métodos | White labels).
//
// Container: resolve a query e distribui para as tabs. A identidade do header
// (nome/CNPJ) é READ-ONLY por contrato — dado legal, alterado por fluxo próprio.
import { LayersIcon } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"
import { useUrlTab } from "@/hooks/use-url-tab"
import { cn } from "@/lib/utils"
import { useAdminMerchant } from "@/services/admin/merchants"

import { formatDocument, merchantInitials } from "../utils"
import { MerchantAddressTab } from "./merchant-address-tab"
import { MerchantBankTab } from "./merchant-bank-tab"
import { MerchantDataTab } from "./merchant-data-tab"
import { MerchantMethodsTab } from "./merchant-methods-tab"
import { MerchantMtlsCertificatesTab } from "./merchant-mtls-certificates-tab"
import { MerchantWhiteLabelsTab } from "./merchant-white-labels-tab"

const panelPrefix = "pages.adminMerchants"

const TAB_VALUES = [
  "data",
  "address",
  "bank",
  "methods",
  "whiteLabels",
  "mtlsCertificates",
] as const
const DEFAULT_TAB = "data"

type MerchantDetailProps = {
  uuid: string
}

// A aba escolhida vive na URL (?tab=): é o que faz um link de fora — como o
// atalho do painel de processors — cair na aba certa, e o que preserva a aba ao
// voltar do detalhe de um certificado.
export const MerchantDetail = ({
  uuid,
}: MerchantDetailProps) => {
  const { t } = useTranslation()
  // `?tab=` é o que faz um link de fora cair na aba certa; sem ele o atalho do
  // painel de processors abriria a ficha na primeira aba e o operador teria de
  // procurar o cofre. Pelo hook compartilhado: antes, um valor desconhecido na
  // URL renderizava as abas SEM conteúdo nenhum ativo — tela em branco.
  const [activeTab, handleTabChange] = useUrlTab(
    TAB_VALUES,
    DEFAULT_TAB
  )
  const { data, isLoading, isError, refetch } =
    useAdminMerchant(uuid)

  if (isLoading) {
    return (
      <div
        data-testid="merchant-detail-skeleton"
        className="space-y-4"
      >
        <Skeleton className="h-20 w-full rounded-xl" />
        <Skeleton className="h-10 w-96 rounded-md" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-6">
        <p className="text-center text-sm text-red-700">
          {t(`${panelPrefix}.detail.loadError`)}
        </p>
        <Button
          type="button"
          variant="outline"
          onClick={() => refetch()}
        >
          {t(`${panelPrefix}.retry`)}
        </Button>
      </div>
    )
  }

  const active = Boolean(data.active)
  const displayName =
    data.fantasyName ||
    data.name ||
    t(`${panelPrefix}.unnamed`)
  // Razão social só como sublinha quando o nome exibido é a fantasia (senão seria
  // redundante). Antes o nome legal ficava ESCONDIDO — numa ficha cadastral, não pode.
  const legalName =
    data.name && data.name !== displayName
      ? data.name
      : null
  // Metadados glanceable do topo: CNPJ (mono) · e-mail · categoria — só os presentes.
  const metaParts = [
    formatDocument(data.document),
    data.email,
    data.category?.name,
  ].filter((part): part is string => Boolean(part))
  const whiteLabelsCount = data.whiteLabels?.length ?? 0

  return (
    <div className="space-y-4">
      <div
        data-testid="merchant-detail-header"
        className={cn(
          "flex flex-wrap items-center gap-4 rounded-xl border border-l-4 bg-card p-4 shadow-sm",
          active
            ? "border-l-emerald-500"
            : "border-l-zinc-300"
        )}
      >
        <Avatar className="h-14 w-14 rounded-lg">
          {data.thumbUrl && (
            <AvatarImage
              src={data.thumbUrl}
              alt={displayName}
              className="object-contain"
            />
          )}
          <AvatarFallback
            className={cn(
              "rounded-lg text-sm font-semibold",
              active
                ? "bg-emerald-100 text-emerald-700"
                : "bg-zinc-100 text-zinc-600"
            )}
          >
            {merchantInitials(displayName)}
          </AvatarFallback>
        </Avatar>
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="text-lg font-semibold leading-tight">
              {displayName}
            </span>
            {legalName && (
              <span className="text-xs text-muted-foreground">
                {t(`${panelPrefix}.detail.legalName`)}:{" "}
                {legalName}
              </span>
            )}
          </div>
          {metaParts.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-muted-foreground">
              {metaParts.map((part, index) => (
                <span
                  key={part}
                  className="flex items-center gap-1.5"
                >
                  {index > 0 && (
                    <span
                      aria-hidden="true"
                      className="text-muted-foreground/40"
                    >
                      ·
                    </span>
                  )}
                  <span
                    className={
                      index === 0 ? "font-mono" : undefined
                    }
                  >
                    {part}
                  </span>
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {whiteLabelsCount > 0 && (
            <Badge
              variant="outline"
              className="gap-1.5 rounded-full border-border font-medium text-muted-foreground"
            >
              <LayersIcon
                className="size-3.5"
                aria-hidden="true"
              />
              {t(`${panelPrefix}.detail.whiteLabelsCount`, {
                count: whiteLabelsCount,
              })}
            </Badge>
          )}
          <Badge
            variant="outline"
            className={cn(
              "rounded-full border font-medium",
              active
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : "border-zinc-200 bg-zinc-50 text-zinc-500"
            )}
          >
            {t(
              `${panelPrefix}.${active ? "badgeActive" : "badgeInactive"}`
            )}
          </Badge>
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
      >
        <TabsList>
          <TabsTrigger value="data">
            {t(`${panelPrefix}.tabs.data`)}
          </TabsTrigger>
          <TabsTrigger value="address">
            {t(`${panelPrefix}.tabs.address`)}
          </TabsTrigger>
          <TabsTrigger value="bank">
            {t(`${panelPrefix}.tabs.bank`)}
          </TabsTrigger>
          <TabsTrigger value="methods">
            {t(`${panelPrefix}.tabs.methods`)}
          </TabsTrigger>
          <TabsTrigger value="whiteLabels">
            {t(`${panelPrefix}.tabs.whiteLabels`)}
          </TabsTrigger>
          <TabsTrigger value="mtlsCertificates">
            {t(`${panelPrefix}.tabs.mtlsCertificates`)}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="data" className="pt-2">
          <MerchantDataTab merchant={data} />
        </TabsContent>
        <TabsContent value="address" className="pt-2">
          <MerchantAddressTab merchant={data} />
        </TabsContent>
        <TabsContent value="bank" className="pt-2">
          <MerchantBankTab merchant={data} />
        </TabsContent>
        <TabsContent value="methods" className="pt-2">
          <MerchantMethodsTab uuid={uuid} />
        </TabsContent>
        <TabsContent value="whiteLabels" className="pt-2">
          <MerchantWhiteLabelsTab merchant={data} />
        </TabsContent>
        <TabsContent
          value="mtlsCertificates"
          className="pt-2"
        >
          <MerchantMtlsCertificatesTab />
        </TabsContent>
      </Tabs>
    </div>
  )
}
